/**
 * WebSocket service for real-time communication
 */

import { io, Socket } from 'socket.io-client';

export interface JobProgress {
  current: number;
  total: number;
  message: string;
  percentage: number;
}

export interface Job {
  id: string;
  type: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  data: any;
  result?: any;
  error?: string;
  progress?: JobProgress;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

class WebSocketService {
  private socket: Socket | null = null;
  private jobListeners: Map<string, {
    onProgress?: (job: Job) => void;
    onCompleted?: (job: Job) => void;
    onFailed?: (job: Job) => void;
  }> = new Map();

  connect(): void {
    if (this.socket?.connected) return;

    // Use the same logic as api.ts to get the base URL
    const getApiBaseUrl = (): string => {
      // Check if we're in production (window.APP_ENV is available)
      if (typeof window !== "undefined" && (window as any).APP_ENV?.VITE_API_BASE_URL) {
        const baseUrl = (window as any).APP_ENV.VITE_API_BASE_URL;
        // Ensure it ends with /api
        return baseUrl.endsWith("/api") ? baseUrl : `${baseUrl}/api`;
      }

      // Check build-time environment variable
      if (import.meta.env.VITE_API_BASE_URL) {
        const baseUrl = import.meta.env.VITE_API_BASE_URL;
        return baseUrl.endsWith("/api") ? baseUrl : `${baseUrl}/api`;
      }

      throw new Error("API base URL is not configured. Set VITE_API_BASE_URL environment variable or ensure window.APP_ENV.VITE_API_BASE_URL is available.");
    };

    const apiBaseUrl = getApiBaseUrl();
    const socketUrl = apiBaseUrl.replace('/api', ''); // Remove /api suffix for socket connection

    console.log('WebSocket API_BASE_URL', apiBaseUrl);
    console.log('WebSocket Socket URL', socketUrl);

    this.socket = io(socketUrl, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected to:', socketUrl);
    });

    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    this.socket.on('job-progress', (job: Job) => {
      const listeners = this.jobListeners.get(job.id);
      if (listeners?.onProgress) {
        listeners.onProgress(job);
      }
    });

    this.socket.on('job-completed', (job: Job) => {
      const listeners = this.jobListeners.get(job.id);
      if (listeners?.onCompleted) {
        listeners.onCompleted(job);
      }
    });

    this.socket.on('job-failed', (job: Job) => {
      const listeners = this.jobListeners.get(job.id);
      if (listeners?.onFailed) {
        listeners.onFailed(job);
      }
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  subscribeToJob(
    jobId: string,
    listeners: {
      onProgress?: (job: Job) => void;
      onCompleted?: (job: Job) => void;
      onFailed?: (job: Job) => void;
    }
  ): void {
    if (!this.socket) return;

    this.jobListeners.set(jobId, listeners);
    this.socket.emit('join-job', jobId);
  }

  unsubscribeFromJob(jobId: string): void {
    if (!this.socket) return;

    this.jobListeners.delete(jobId);
    this.socket.emit('leave-job', jobId);
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const webSocketService = new WebSocketService();